import axios from "axios";

import { AgentTypeT } from "../components/common/globalValues";
import { toastify } from "../components/common/notification";

import { url } from ".";

import { LogParam } from "../types/logParam";
import { errorMessage } from "../utils/common";
import { logError, logInfo } from "../utils/logger";
import { apiUnauthLogout } from "./common";

// always set function name and message for every executable block
// data is optional
const logParams: LogParam = {
  applicationName: "Orchestrator Client",
  screenModuleName: "client\\src\\controllers\\organization.ts",
  functionName: "",
  message: "",
};

type DocumentT = {
  _id: string;
};

export interface IOrganizationDetails extends DocumentT {
  name: string;
  type: AgentTypeT;
  deployed: boolean;
  active_status: boolean;
  connected: boolean;
  tasks: { manual_hours: string }[];
  timezone: string;
  runningTask: {
    name: string;
  };
}

interface IAgentTask extends DocumentT {
  task: string;
  imported_by: {
    _id: string;
    name: string;
    email: string;
    role: string;
  };
  imported_by_name: string;
  version: number;
  config: {
    id: string;
    value: string;
  }[];
  manual_hours: number;
}

export interface IAgent extends DocumentT {
  name: string;
  type: AgentTypeT;
  timezone: string;
  granted_organization: string;
  tasks: IAgentTask[];
  active_status: boolean;
  connected: boolean;
  deleted_status: boolean;
  deployed: boolean;
  last_ping: string;
  python_port_alive: boolean;
  runningTask: {
    [x: string]: string;
    name: string;
  };
  created_by: string;
  createdAt: string;
  updatedAt: string;
  agentVersion: string;
}

export const orgListApi = (token: string) =>
  axios
    .get(`${url}/org/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationListApi";
      logParams.message = "retrieved organizations";
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "organizationListApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      if (error.response.status == 401) {
        apiUnauthLogout(error, "organizationListApi");
      } else {
        toastify(
          "failure",
          error.response.data.message.length > 0
            ? error.response.data.message
            : "Something went wrong"
        );
      }
    });

export const organizationListApi = (token: string) =>
  axios
    .get(`${url}/org`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationListApi";
      logParams.message = "retrieved organizations";
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "organizationListApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      if (error.response.status == 401) {
        apiUnauthLogout(error, "organizationListApi");
      } else {
        toastify(
          "failure",
          error.response.data.message.length > 0
            ? error.response.data.message
            : "Something went wrong"
        );
      }
    });

export const createOrganizationApi = (token: string, params: unknown) =>
  axios
    .post(`${url}/org`, params, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "createOrganizationApi";
      logParams.message = "created org";
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "createOrganizationApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const organizationDetailsApi = (token: string, orgId: string) =>
  axios
    .get<{ data: IOrganizationDetails[] }>(`${url}/org/${orgId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationDetailsApi";
      logParams.message = `retrieved organization ${orgId} details`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "organizationDetailsApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      if (error.response.status == 401) {
        apiUnauthLogout(error, "organizationDetailsApi");
      } else {
        toastify(
          "failure",
          error.response.data.message.length > 0
            ? error.response.data.message
            : "Something went wrong"
        );
      }
      return { data: [] };
    });

export const organizationDevelopersApi = (token: string, orgId: string) =>
  axios
    .get(`${url}/user/org/${orgId}/developer`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationDevelopersApi";
      logParams.message = `retrieved organization ${orgId} developers`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "organizationDevelopersApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const organizationBotsListerApi = (token: string, orgId: string) =>
  axios
    .get<{ data: IAgent[] }>(`${url}/agent/${orgId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationBotsListerApi";
      logParams.message = `retrieved organization ${orgId} agents`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "organizationBotsListerApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      if (error.response.status == 401) {
        apiUnauthLogout(error, "organizationBotsListerApi");
      } else {
        toastify(
          "failure",
          error?.response?.data?.message?.length > 0
            ? error.response.data.message
            : "Something went wrong"
        );
      }
      return { data: [] };
    });

export const botsListApi = (token: string) =>
  axios
    .get(`${url}/agent`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "botsListApi";
      logParams.message = `retrieved agent`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "botsListApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const organizationAdminApi = (token: string, orgId: string) =>
  axios
    .get(`${url}/user/org/${orgId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationAdminApi";
      logParams.message = `retrieved org ${orgId} admin`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "organizationAdminApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const updateOrgApi = (token: string, data: unknown, id: string) =>
  axios
    .put(`${url}/org/${id}`, data, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "updateOrgApi";
      logParams.message = `updated org ${id}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "updateOrgApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const deleteOrgApi = (token: string, id: string) =>
  axios
    .delete(`${url}/org/${id}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "deleteOrgApi";
      logParams.message = `deleted org ${id}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "deleteOrgApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const organizationQueueApi = (token: string, id: string) =>
  axios
    .get(`${url}/queue/${id}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationQueueApi";
      logParams.message = `retrieved org ${id} queue`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "organizationQueueApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const addOrganizationQueueApi = (token: string, data: unknown) =>
  axios
    .post(`${url}/queue/`, data, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "addOrganizationQueueApi";

      if (res.status === 201) {
        logParams.message = `created queue for org`;
        logInfo(logParams);
        return res.data;
      }
      logParams.message = `created queue for org failed`;
      logError(logParams);

      toastify("failure", res.data);
      return undefined;
    })
    .catch((error) => {
      logParams.functionName = "addOrganizationQueueApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const organizationAPIKeyApi = (token: string, id: string) =>
  axios
    .get(`${url}/apiKey/${id}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationAPIKeyApi";
      logParams.message = `retrieved org ${id} API Key`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "organizationAPIKeyApi";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const createAPIKey = (token: string, data: unknown) =>
  axios
    .post(`${url}/apiKey/generate`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "createAPIKey";
      logParams.message = `created API key`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "createAPIKey";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const updateAPIKey = (token: string, data: unknown) =>
  axios
    .put(`${url}/apiKey/update`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "updateAPIKey";
      logParams.message = `update API key`;
      logInfo(logParams);
      console.log("res", res);
      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "updateAPIKey";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const apiKeyDeleteApi = (token: string, data: unknown) =>
  axios
    .post(`${url}/apiKey/delete`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "deleteAPIKey";
      logParams.message = `deleted API key`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "deleteAPIKey";
      logParams.message = errorMessage(error);
      logError(logParams);
      console.log("error", error.response);
      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const clearQueueDataApi = (token: string, queueName: string) =>
  axios
    .post(
      `${url}/queue/${queueName}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      logParams.functionName = "clearQueueDataApi";
      logParams.message = `clear Queue Data Api`;
      logInfo(logParams);
      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "clearQueueDataApi";
      logParams.message = errorMessage(error);
      logError(logParams);
      console.log("error", error.response);
      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });

export const getActiveAgentCount = (token: string, orgId: string) =>
  axios
    .get(`${url}/agent/${orgId}/active/count`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "getActiveAgentCount";
      logParams.message = `retrieved active agent count for org ${orgId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      logParams.functionName = "getActiveAgentCount";
      logParams.message = errorMessage(error);
      logError(logParams);

      toastify(
        "failure",
        error.response.data.message.length > 0
          ? error.response.data.message
          : "Something went wrong"
      );
    });
